import React, { memo, useMemo } from "react";
import { Box, Typography, Divider, Fade } from "@mui/material";
import { DragIndicator } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import ProgressCircle from "components/ProgressCircle";
import FlexBetween from "components/FlexBetween";
import FlexColumn from "components/FlexColumn";


const DEFAULT_ICON = (
  <DragIndicator sx={{ color: "green.dark", fontSize: 24 }} />
);

/* ================= DEEP COMPARE ================= */
const isEqual = (a, b) => {
  if (a === b) return true;

  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  )
    return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (let key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!isEqual(a[key], b[key])) return false;
  }

  return true;
};

/* ================= TYPE DETECTOR ================= */
const detectMode = (props) => {
  if (typeof props === "function") return "custom";

  if (!props || typeof props !== "object") return "empty";

  if ("divisor" in props || "progress" in props) return "progress";

  return "list";
};

/* ================= LIST RENDER ================= */
const ListContent = memo(({ data, icon }) => (
  <FlexBetween>
      <Box>{icon}</Box>
      <Box
        flexGrow={1}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        ml={1}
        gap={1}
        sx={{ overflowY: "auto" }}
      >
        {Object.entries(data).map(([key, value]) => (
          <FlexBetween key={key}>
            <Typography variant="h6">{key}:</Typography>
            <Typography variant="h5" sx={{ textAlign: "right" }}>
              {value}
            </Typography>
          </FlexBetween>
        ))}
      </Box>
    </FlexBetween>  
));

/* ================= PROGRESS RENDER ================= */
const ProgressContent = memo(({ data, icon }) => (
  <>
    <Box
      display="flex"
      justifyContent="space-between"
      minHeight={60}
    >
      <FlexBetween>{icon}</FlexBetween>

      <FlexColumn flex={data.progress === null ? 1 : null}>
        <Typography variant="h5">{data.divisor}</Typography>

        {data.dividend && (
          <Divider
            flexItem
            sx={{ my: 0.5, borderColor: "text.primary" }}
          />
        )}

        <Typography variant="h5">{data.dividend}</Typography>
      </FlexColumn>

      {data.progress !== null && (
        <FlexBetween position="relative">
          <ProgressCircle progress={data.progress || 0} />

          <Box
            position="absolute"
            top="50%"
            left="50%"
            textAlign="center"
            sx={{ transform: "translate(-50%, -50%)" }}
          >
            <Typography variant="h6" color="yellow.darkest">
              {data.rate}
            </Typography>
          </Box>
        </FlexBetween>
      )}
    </Box>

    {data.title && (
      <Typography variant="h6">{data.title}</Typography>
    )}
  </>
));

/* ================= MAIN ================= */
const StatBox = ({ icon = DEFAULT_ICON, props }) => {
  const mode = useMemo(() => detectMode(props), [props]);

  const content = useMemo(() => {
    switch (mode) {
      case "custom":
        return props();

      case "list":
        return (
          <ListContent data={props} icon={icon} />          
        );

      case "progress":
        return <ProgressContent data={props} icon={icon} />;

      default:
        return null;
    }
  }, [mode, props, icon]);

  return (
    <Box
      sx={{
        display: 'flex',         
        flexDirection: 'column',
        justifyContent: 'space-around',
        borderRadius: 1,
        border: "1px solid",
        borderColor: "neutral.deep",
        backgroundColor: "slate.lightest",
        p: 1,
        overflow: "hidden",
      }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={mode}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.25 }}
        >
          <Fade in timeout={200}>
            <Box>{content}</Box>
          </Fade>
        </motion.div>
      </AnimatePresence>
    </Box>
  );
};


export default memo(StatBox, (prev, next) => {
  return isEqual(prev.props, next.props) && prev.icon === next.icon;
});